import { useEffect, useRef, Children, isValidElement } from 'react'
import { Menu } from '@/common/ui/Menu'
import { cn } from '@/common/lib/cn/cn'
import './Dropdown.css'

export type DropdownProps = {
    children?: React.ReactNode
    className?: string
}

export const Dropdown = ({ children, className, ...props }: DropdownProps) => {
    const detailsRef = useRef<HTMLDetailsElement>(null)

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && detailsRef.current?.open) {
                detailsRef.current.open = false
            }
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (detailsRef.current && !detailsRef.current.contains(event.target as Node)) {
                detailsRef.current.open = false
            }
        }

        // Новый обработчик для кликов внутри
        const handleClickInside = (event: MouseEvent) => {
            if (detailsRef.current?.open) {
                const target = event.target as HTMLElement

                // Закрыть если клик по элементу меню, кнопке или ссылке
                if (
                    target.closest('a') ||
                    target.closest('button') ||
                    target.closest('label') ||
                    target.closest('.menu-item') ||
                    target.closest('[data-close-dropdown]')
                ) {
                    detailsRef.current.open = false
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleClickOutside)

        const detailsEl = detailsRef.current
        if (detailsEl) {
            detailsEl.addEventListener('click', handleClickInside)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handleClickOutside)
            if (detailsEl) {
                detailsEl.removeEventListener('click', handleClickInside)
            }
        }
    }, [])

    const validateChildren = () => {
        const childrenArray = Children.toArray(children)

        if (childrenArray.length < 2) {
            console.warn('Dropdown must contain at least 2 elements: <summary /> and .dropdown-content')
            return children
        }

        const [firstChild, secondChild] = childrenArray

        const isSummaryElement =
            isValidElement(firstChild) &&
            (firstChild.type === 'summary' || firstChild.type === DropdownSummary || firstChild.props?.as === 'summary')

        if (!isSummaryElement) {
            console.warn('First element of Dropdown must be a <summary> tag')
        }

        if (!isValidElement(secondChild)) {
            console.warn('Second element of Dropdown must be a React element')
            return children
        }

        const isDomElement = typeof secondChild.type === 'string'
        if (isDomElement) {
            const secondChildClassName = secondChild.props?.className || ''
            if (!secondChildClassName.includes('dropdown-content')) {
                console.warn('Second element of Dropdown must contain "dropdown-content" class')
            }
        }

        return children
    }

    const detailsClasses = cn('dropdown', className)

    return (
        <details ref={detailsRef} className={detailsClasses} {...props}>
            {validateChildren()}
        </details>
    )
}

export type DropdownMenuProps = {
    children?: React.ReactNode
    className?: string
}

export const DropdownMenu = ({ children, className }: DropdownMenuProps) => {
    const handleClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement

        // Если элемент помечен для автозакрытия
        if (target.closest('[data-close-dropdown]')) {
            const dropdown = target.closest('details.dropdown') as HTMLDetailsElement
            if (dropdown) {
                dropdown.open = false
            }
        }
    }

    const menuClasses = cn('menu dropdown-content shadow-sm', className)
    return (
        <Menu className={menuClasses} onClick={handleClick}>
            {children}
        </Menu>
    )
}

export type DropdownSummaryProps = {
    className?: string
    children?: React.ReactNode
}

export const DropdownSummary = ({ className, children }: DropdownSummaryProps) => {
    const summaryClasses = cn('', className)
    return <summary className={summaryClasses}>{children}</summary>
}
